"use client";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";
import { ParentProfileSchema } from "src/mocks";
import { NunitoFont } from "@/src/fonts/fonts-config";
import { SideBar, Navbar } from "@/src/components";
import Accordion from "./accordion";
import FormContainer from "../../profile/create/form-container";
import {
  getUserInfoFromStorage,
  saveUserProfileToStorage,
} from "@/src/utils/storage";
import { useQuery } from "@tanstack/react-query";
import { FetchMyProfile } from "@/src/services/profile/profile";
import FormOne from "./form-one";
import FormTwo from "./form-two";
import FormThree from "./form-three";
import FormFour from "./form-four";
import FormFive from "./form-five";

const ParentProfile = () => {
  let [userId, setUserId] = useState(false);

  const { data: userProfileData, isSuccess: qSavingsIsSuccess } = useQuery({
    queryKey: ["getUserProfile"],
    queryFn: () => FetchMyProfile(userId as unknown as string),
    enabled: Boolean(userId),
  });

  useEffect(() => {
    // Move storage access to useEffect to avoid hydration issues
    const userInfo = getUserInfoFromStorage();
    setUserId(userInfo.id || "");
  }, []);
  //   console.log(userProfileData);
  //   console.log(userProfileData?.data?.data?.profileData);

  const [currentStep, setCurrentStep] = useState(1);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [utilityBillUrl, setUtilityBillUrl] = useState<string>("");
  const [currentMaritalStatus, setCurrentMaritalStatus] = useState({
    label: "",
    value: "",
  });
  const [currentGender, setCurrentGender] = useState({ label: "", value: "" });

  const [currentResident, setCurrentResident] = useState({
    label: "",
    value: "",
  });
  const [currentIdentity, setCurrentIdentity] = useState({
    label: "",
    value: "",
  });
  const [currentOccupation, setCurrentOccupation] = useState({
    label: "",
    value: "",
  });
  const [currentIncome, setCurrentIncome] = useState({
    label: "",
    value: "",
  });
  const [currentPayment, setCurrentPayment] = useState({
    label: "",
    value: "",
  });
  const [currentEmployement, setCurrentEmployement] = useState({
    label: "",
    value: "",
  });
  const [currentAccount, setCurrentAccount] = useState({
    label: "",
    value: "",
  });
  const [currentBank, setCurrentBank] = useState({
    label: "",
    value: "",
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
    getValues,
    resetField,
    trigger,
    control,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(ParentProfileSchema),
    mode: "onChange",
    defaultValues: userProfileData?.data?.data?.profileData,
  });

  useFormPersist("parent-profile-page", {
    watch,
    setValue,
  });

  useEffect(() => {
    const storedFormValues = userProfileData?.data?.data?.profileData;
    // console.log("setting profile value");
    setCurrentMaritalStatus({
      label: storedFormValues?.maritalStatus?.toUpperCase(),
      value: storedFormValues?.maritalStatus,
    });
    setCurrentGender({
      label: storedFormValues?.gender?.toUpperCase(),
      value: storedFormValues?.gender,
    });
    setCurrentIdentity({
      label: storedFormValues?.proofOfIdentity?.toUpperCase(),
      value: storedFormValues?.proofOfIdentity,
    });
    setCurrentResident({
      label: storedFormValues?.residentialStatus?.toUpperCase(),
      value: storedFormValues?.residentialStatus,
    });
    setCurrentOccupation({
      label: storedFormValues?.occupation?.toUpperCase(),
      value: storedFormValues?.occupation,
    });
    setCurrentIncome({
      label: storedFormValues?.grossAnnualIncome?.toUpperCase(),
      value: storedFormValues?.grossAnnualIncome,
    });
    setCurrentPayment({
      label: storedFormValues?.frequencyOfPayment?.toUpperCase(),
      value: storedFormValues?.frequencyOfPayment,
    });
    setCurrentEmployement({
      label: storedFormValues?.employmentStatus?.toUpperCase(),
      value: storedFormValues?.employmentStatus,
    });
    setCurrentAccount({
      label: storedFormValues?.accountType?.toUpperCase(),
      value: storedFormValues?.accountType,
    });
    setCurrentBank({
      label: storedFormValues?.bankName?.toUpperCase(),
      value: storedFormValues?.bankName,
    });
    setImageUrl(storedFormValues?.proofOfIdentityDocumentURL);
    setUtilityBillUrl(storedFormValues?.utilityBillDocumentURL);
    const timeout = setTimeout(function () {
      saveUserProfileToStorage(storedFormValues);
    }, 5000);
  }, [setValue, userProfileData]);

  const items = [
    {
      id: "accordion-1",
      title: "Identitiy Details",
      content: (
        <FormOne
          getValues={getValues}
          register={register}
          errors={errors}
          isValid={isValid}
          resetField={resetField}
          control={control}
          trigger={trigger}
          // nextStep={nextStep}
          setValue={setValue}
          currentIdentity={currentIdentity}
          currentGender={currentGender}
          currentMaritalStatus={currentMaritalStatus}
          currentResident={currentResident}
          setCurrentGender={setCurrentGender}
          setCurrentIdentity={setCurrentIdentity}
          setCurrentMaritalStatus={setCurrentMaritalStatus}
          setCurrentResident={setCurrentResident}
          imageUrl={imageUrl}
          setImageUrl={setImageUrl}
          utilityBillUrl={utilityBillUrl}
          setUtilityBillUrl={setUtilityBillUrl}
        />
      ),
    },
    {
      id: "accordion-2",
      title: "Occupational Details",
      content: (
        <FormTwo
          getValues={getValues}
          resetField={resetField}
          errors={errors}
          isValid={isValid}
          control={control}
          trigger={trigger}
          // nextStep={nextStep}
          setValue={setValue}
          register={register}
          currentOccupation={currentOccupation}
          setCurrentOccupation={setCurrentOccupation}
          currentIncome={currentIncome}
          setCurrentIncome={setCurrentIncome}
          currentPayment={currentPayment}
          setCurrentPayment={setCurrentPayment}
          currentEmployment={currentEmployement}
          setCurrentEmployment={setCurrentEmployement}
          currentBank={currentBank}
          currentAccount={currentAccount}
          setCurrentBank={setCurrentBank}
          setCurrentAccount={setCurrentAccount}
        />
      ),
    },
    {
      id: "accordion-3",
      title: "Details of reference",
      content: (
        <FormThree
          getValues={getValues}
          register={register}
          errors={errors}
          isValid={isValid}
          resetField={resetField}
          control={control}
          trigger={trigger}
          // nextStep={nextStep}
          setValue={setValue}
        />
      ),
    },
    {
      id: "accordion-4",
      title: "Medical Questionnaire",
      content: (
        <FormFour
          resetField={resetField}
          // nextStep={nextStep}
          getValues={getValues}
          isValid={isValid}
          trigger={trigger}
          register={register}
          errors={errors}
          setValue={setValue}
          control={control}
        />
      ),
    },
    {
      id: "accordion-5",
      title: "Physician details",
      content: (
        <FormFive
          //   nextStep={nextStep}
          getValues={getValues}
          resetField={resetField}
          isValid={isValid}
          trigger={trigger}
          register={register}
          errors={errors}
          setValue={setValue}
          control={control}
        />
      ),
    },
  ];

  return (
    <div
      className={`flex flex-col items-center min-h-screen ${NunitoFont.className}`}
    >
      <div className="w-full max-w-sm bg-[#f5fffb] px-5 has-[*:checked]:overflow-hidden relative h-full flex-1 flex flex-col">
        <Navbar
        //  prevStep={prevStep} currentStep={currentStep}
        />
        {/* <main className="flex-grow">{renderStep()}</main> */}
        <FormContainer
          title="Your details"
          subTitle="Below are all your details, click on each dropdown to view details"
        >
          <Accordion items={items} />
        </FormContainer>
        <SideBar />
      </div>
    </div>
  );
};

export default ParentProfile;
